import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import { apiClient } from '../../lib/api';

interface UpgradePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: 'free' | 'starter' | 'professional' | 'enterprise';
}

interface Plan {
  id: 'free' | 'starter' | 'professional' | 'enterprise';
  name: string;
  price: number;
  tokens: number;
  features: string[];
  recommended?: boolean;
}

export default function UpgradePlanModal({ isOpen, onClose, currentPlan }: UpgradePlanModalProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const plans: Plan[] = [
    {
      id: 'free',
      name: t('billing.plans.free.name', 'Free'),
      price: 0,
      tokens: 0,
      features: [
        t('billing.plans.free.feature1', '0 tokens per month'),
        t('billing.plans.free.feature2', 'Purchase token packs'),
        t('billing.plans.free.feature3', '1 concurrent job'),
        t('billing.plans.free.feature4', 'Community support'),
      ],
    },
    {
      id: 'starter',
      name: t('billing.plans.starter.name', 'Starter'),
      price: 29,
      tokens: 1000,
      features: [
        t('billing.plans.starter.feature1', '1,000 tokens per month'),
        t('billing.plans.starter.feature2', '2 concurrent jobs'),
        t('billing.plans.starter.feature3', '5 jobs per second'),
        t('billing.plans.starter.feature4', 'Email support'),
      ],
    },
    {
      id: 'professional',
      name: t('billing.plans.professional.name', 'Professional'),
      price: 99,
      tokens: 5000,
      recommended: true,
      features: [
        t('billing.plans.professional.feature1', '5,000 tokens per month'),
        t('billing.plans.professional.feature2', '5 concurrent jobs'),
        t('billing.plans.professional.feature3', '10 jobs per second'),
        t('billing.plans.professional.feature4', 'Priority email support'),
        t('billing.plans.professional.feature5', 'Advanced analytics'),
      ],
    },
    {
      id: 'enterprise',
      name: t('billing.plans.enterprise.name', 'Enterprise'),
      price: 299,
      tokens: 20000,
      features: [
        t('billing.plans.enterprise.feature1', '20,000 tokens per month'),
        t('billing.plans.enterprise.feature2', '20 concurrent jobs'),
        t('billing.plans.enterprise.feature3', '100 jobs per second'),
        t('billing.plans.enterprise.feature4', 'Dedicated support'),
        t('billing.plans.enterprise.feature5', 'Custom integrations'),
        t('billing.plans.enterprise.feature6', 'SLA guarantee'),
      ],
    },
  ];

  const handleUpgrade = async (planId: 'free' | 'starter' | 'professional' | 'enterprise') => {
    // Can't "upgrade" to free
    if (planId === 'free') {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/billing/subscription/upgrade', {
        plan: planId,
      });

      const checkoutUrl = response.data?.data?.checkoutUrl || response.data?.checkoutUrl;
      if (checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = checkoutUrl;
      } else {
        setError(t('billing.upgrade.noCheckoutUrl', 'Failed to create checkout session'));
      }
    } catch (err: any) {
      console.error('Failed to upgrade plan:', err);
      setError(err.response?.data?.message || t('billing.upgrade.error', 'Failed to start upgrade process'));
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-700 rounded-lg max-w-7xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b-2 border-gray-300 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold dark:text-white">
              {t('billing.upgrade.title', 'Upgrade Your Plan')}
            </h2>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('billing.upgrade.subtitle', 'Choose the plan that fits your needs')}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-6 p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
            <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Plans Grid */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const isCurrent = currentPlan === plan.id;
            return (
              <div
                key={plan.id}
                className={`relative border-2 rounded-lg p-6 ${
                  isCurrent
                    ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : plan.recommended
                    ? 'border-black dark:border-white bg-gray-50 dark:bg-gray-800'
                    : 'border-gray-300 dark:border-gray-700'
                }`}
              >
                {/* Current Plan Badge */}
                {isCurrent && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 dark:bg-blue-400 text-white dark:text-blue-900 px-3 py-1 text-xs font-bold rounded-full">
                      {t('billing.upgrade.currentPlan', 'CURRENT PLAN').toUpperCase()}
                    </span>
                  </div>
                )}

                {/* Recommended Badge */}
                {!isCurrent && plan.recommended && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-black dark:bg-white text-white dark:text-black px-3 py-1 text-xs font-bold rounded-full">
                      {t('billing.upgrade.recommended', 'RECOMMENDED')}
                    </span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold dark:text-white mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold dark:text-white">€{plan.price}</span>
                    <span className="text-gray-600 dark:text-gray-400">/month</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {plan.tokens.toLocaleString()} {t('common.tokens', 'tokens')}/month
                  </p>
                </div>

                {/* Features List */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <svg
                        className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Upgrade Button */}
                <Button
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={isLoading || isCurrent}
                  variant={plan.recommended ? 'primary' : 'secondary'}
                  className="w-full"
                >
                  {isLoading
                    ? t('common.loading', 'Loading...')
                    : isCurrent
                    ? t('billing.upgrade.currentPlan', 'Current Plan')
                    : t('billing.upgrade.selectPlan', 'Select Plan')}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t-2 border-gray-300 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            {t(
              'billing.upgrade.footer',
              'All plans include access to all features. You can upgrade, downgrade, or cancel anytime.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
